import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Lock, Loader2, Shield } from 'lucide-react';
import { companyInfo } from '../mock';

export const AdminLogin = () => {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const formatError = (detail) => {
    if (!detail) return 'Login failed. Please try again.';
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(' ');
    return String(detail);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await login(email, password);
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900 px-4">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-8">
          <img src={companyInfo.logoUrl} alt="PWCA" className="h-16 w-16 object-contain mb-3" />
          <h1 className="text-2xl font-black text-white text-center">Phoenix WellControl Academy</h1>
          <p className="text-orange-300 font-semibold tracking-wider text-sm">ADMIN PANEL</p>
        </div>
        <Card className="border-0 shadow-2xl overflow-hidden">
          <div className="h-2 bg-gradient-to-r from-orange-500 to-yellow-500"></div>
          <CardHeader className="bg-slate-800 text-white p-6">
            <CardTitle className="text-xl font-bold flex items-center">
              <Shield className="h-6 w-6 mr-2 text-orange-400" />
              Sign In
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 bg-white">
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="admin-login-form">
              <div className="space-y-2">
                <Label htmlFor="admin-email" className="text-gray-700 font-semibold">Email</Label>
                <Input
                  id="admin-email"
                  type="email"
                  data-testid="admin-email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="admin@phoenixwellcontrol.com"
                  className="border-2 border-gray-200 focus:border-orange-500 rounded-xl p-3"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="admin-password" className="text-gray-700 font-semibold">Password</Label>
                <Input
                  id="admin-password"
                  type="password"
                  data-testid="admin-password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="Enter your password"
                  className="border-2 border-gray-200 focus:border-orange-500 rounded-xl p-3"
                />
              </div>
              {error && (
                <p data-testid="admin-login-error" className="text-red-600 text-sm font-medium">{error}</p>
              )}
              <Button
                type="submit"
                disabled={submitting}
                data-testid="admin-login-button"
                className="w-full bg-gradient-to-r from-orange-500 to-yellow-500 hover:from-orange-600 hover:to-yellow-600 text-white font-bold py-5 rounded-xl"
              >
                {submitting ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : <Lock className="h-5 w-5 mr-2" />}
                {submitting ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
